import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { router } from 'expo-router';
import * as Application from 'expo-application';
import Constants from 'expo-constants';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  retryCount: number;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });

    // Log detailed error information
    console.error('🚨 ErrorBoundary caught an error:', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      platform: Constants.platform,
      executionEnvironment: Constants.executionEnvironment,
      appVersion: Application.nativeApplicationVersion,
      retryCount: this.state.retryCount
    });

    // Report Expo Go specific errors
    if (this.isExpoGoError(error)) {
      console.error('📱 Expo Go compatibility error detected:', error.message);
      console.log('💡 This error is likely due to missing native modules in Expo Go');
    }

    // Report OneSignal specific errors
    if (this.isOneSignalError(error)) {
      console.error('🔔 OneSignal-related error detected:', error.message);
      console.log('💡 OneSignal errors are usually safe to ignore - push notifications will be disabled');
      console.log('📱 The app will continue to work normally without push notifications');
    }

    // Report network-related errors
    if (this.isNetworkError(error)) {
      console.error('📡 Network-related error detected:', error.message);
      console.log('💡 Check your internet connection and API endpoints');
    }

    // Report critical auth errors separately
    if (this.isAuthError(error)) {
      console.error('🔐 Auth-related error detected:', error.message);
    }

    // Report cross-platform compatibility errors
    if (this.isCrossPlatformError(error)) {
      console.error('🌐 Cross-platform compatibility error:', error.message);
    }
  }

  isExpoGoError = (error: Error): boolean => {
    const expoGoErrorKeywords = [
      'native module',
      'expo go',
      'development build',
      'onesignal',
      'invariant violation',
      'expopushtokenmanager',
      'cannot find native module'
    ];
    
    const errorMessage = error.message?.toLowerCase() || '';
    return expoGoErrorKeywords.some(keyword => errorMessage.includes(keyword));
  };

  isOneSignalError = (error: Error): boolean => {
    const oneSignalErrorKeywords = [
      'onesignal',
      'notification',
      'push',
      'initialize',
      'setappid',
      'prompt',
      'permission',
      'listeners',
      'expopushtokenmanager', // Add ExpoPushTokenManager as notification-related
      'native module'
    ];
    
    const errorMessage = error.message?.toLowerCase() || '';
    const stackTrace = error.stack?.toLowerCase() || '';
    
    return oneSignalErrorKeywords.some(keyword => 
      errorMessage.includes(keyword) || stackTrace.includes(keyword)
    );
  };

  isNetworkError = (error: Error): boolean => {
    const networkErrorKeywords = [
      'fetch',
      'network',
      'timeout',
      'connection',
      'cors'
    ];
    
    const errorMessage = error.message?.toLowerCase() || '';
    return networkErrorKeywords.some(keyword => errorMessage.includes(keyword));
  };

  isAuthError = (error: Error): boolean => {
    const authErrorKeywords = [
      'supabase',
      'auth',
      'session',
      'login',
      'signup',
      'token',
      'authentication',
      'authorization',
      'gotrue',
      'validation failed'
    ];

    const errorMessage = error.message?.toLowerCase() || '';
    const stackTrace = error.stack?.toLowerCase() || '';
    
    return authErrorKeywords.some(keyword => 
      errorMessage.includes(keyword) ||
      stackTrace.includes(keyword)
    );
  };

  isCrossPlatformError = (error: Error): boolean => {
    const crossPlatformKeywords = [
      'window is not defined',
      'localStorage',
      'sessionStorage',
      'document is not defined',
      'navigator is not defined',
      'XMLHttpRequest',
      'fetch is not defined'
    ];

    return crossPlatformKeywords.some(keyword => 
      error.message?.toLowerCase().includes(keyword.toLowerCase()) ||
      error.stack?.toLowerCase().includes(keyword.toLowerCase())
    );
  };

  isContextError = (error: Error): boolean => {
    const contextKeywords = [
      'context',
      'provider',
      'usecontext',
      'cannot read properties of undefined',
      'cannot read property',
      'authcontext',
      'notificationcontext',
      'sobrietycontext'
    ];

    return contextKeywords.some(keyword => 
      error.message?.toLowerCase().includes(keyword.toLowerCase()) ||
      error.stack?.toLowerCase().includes(keyword.toLowerCase())
    );
  };

  isOneSignalError = (error: Error): boolean => {
    const oneSignalKeywords = [
      'onesignal',
      'push notification',
      'notification permission',
      'device registration'
    ];

    return oneSignalKeywords.some(keyword => 
      error.message?.toLowerCase().includes(keyword.toLowerCase()) ||
      error.stack?.toLowerCase().includes(keyword.toLowerCase())
    );
  };

  getErrorMessage = (): string => {
    if (!this.state.error) return 'Erro desconhecido';

    const error = this.state.error;

    // Auth-specific error messages
    if (this.isAuthError(error)) {
      if (error.message.includes('session')) {
        return 'Problema com a sessão de usuário. Vamos tentar reconectar.';
      }
      if (error.message.includes('network') || error.message.includes('fetch')) {
        return 'Problema de conexão com o servidor. Verifique sua internet.';
      }
      return 'Erro de autenticação. Vamos tentar reconectar.';
    }

    // Cross-platform error messages
    if (this.isCrossPlatformError(error)) {
      return 'Problema de compatibilidade detectado. Recarregando a aplicação.';
    }

    // Generic error message
    if (error.message.includes('Network')) {
      return 'Problema de conexão. Verifique sua internet e tente novamente.';
    }

    return 'Ocorreu um erro inesperado. Vamos tentar resolver isso.';
  };

  getActionText = (): string => {
    if (this.state.retryCount >= 3) {
      return 'Reiniciar App';
    }
    if (this.isAuthError(this.state.error)) {
      return 'Reconectar';
    }
    if (this.isCrossPlatformError(this.state.error)) {
      return 'Recarregar';
    }
    return 'Tentar Novamente';
  };

  handleRetry = async () => {
    const newRetryCount = this.state.retryCount + 1;

    console.log(`🔄 ErrorBoundary: Retry attempt ${newRetryCount}`);

    this.setState({ 
      hasError: false, 
      error: undefined, 
      errorInfo: undefined,
      retryCount: newRetryCount 
    });

    // For auth errors, redirect to welcome/login
    if (this.isAuthError(this.state.error)) {
      console.log('🔐 Redirecting to welcome due to auth error');
      router.replace('/welcome');
      return;
    }

    // For cross-platform errors, try to reload the current route
    if (this.isCrossPlatformError(this.state.error)) {
      console.log('🌐 Reloading due to cross-platform error');
      // Force a hard reload by going to index and back
      router.replace('/');
      return;
    }

    // For repeated failures, restart the app
    if (newRetryCount >= 3) {
      console.log('🔄 Too many retries, restarting app');
      router.replace('/welcome');
      return;
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <View style={styles.container}>
          <ScrollView contentContainerStyle={styles.scrollContent}>
            <Text style={styles.title}>Oops! Algo deu errado</Text>

            <Text style={styles.message}>
              {this.getErrorMessage()}
            </Text>

            {this.state.retryCount > 0 && (
              <Text style={styles.retryInfo}>
                Tentativa {this.state.retryCount} de 3
              </Text>
            )}

            <TouchableOpacity
              style={styles.button}
              onPress={this.handleRetry}
            >
              <Text style={styles.buttonText}>
                {this.getActionText()}
              </Text>
            </TouchableOpacity>

            {__DEV__ && this.state.error && (
              <View style={styles.debugInfo}>
                <Text style={styles.debugTitle}>Debug Info:</Text>
                <Text style={styles.debugText}>
                  {this.state.error.message}
                </Text>
                {this.state.error.stack && (
                  <Text style={styles.debugStack}>
                    {this.state.error.stack.substring(0, 500)}...
                  </Text>
                )}
              </View>
            )}
          </ScrollView>
        </View>
      );
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#f5f5f5',
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 10,
    textAlign: 'center',
  },
  message: {
    fontSize: 16,
    marginBottom: 20,
    textAlign: 'center',
  },
  retryInfo: {
    fontSize: 14,
    color: 'gray',
    marginBottom: 15,
  },
  button: {
    backgroundColor: '#4682B4',
    paddingVertical: 12,
    paddingHorizontal: 25,
    borderRadius: 5,
    marginBottom: 15,
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  debugInfo: {
    marginTop: 20,
    padding: 15,
    backgroundColor: '#eee',
    borderRadius: 5,
  },
  debugTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  debugText: {
    fontSize: 14,
  },
  debugStack: {
    fontSize: 12,
    color: 'gray',
  },
});